'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  RefreshCw,
  Home,
  TrendingUp,
  Users,
  Activity,
  Bug,
} from 'lucide-react';
import { usePrivy } from '@privy-io/react-auth';
import { createLogger } from '@/lib/logger';
import { useT } from '@/hooks/useT';

const logger = createLogger('ErrorState');

interface ErrorStateProps {
  error: Error & { digest?: string };
  reset: () => void;
  title?: string;
  description?: string;
  section?: string;
}

const SHORTCUTS = [
  { href: '/markets', tKey: 'error.shortcut.markets', Icon: TrendingUp },
  { href: '/traders', tKey: 'error.shortcut.traders', Icon: Users },
  { href: '/activity', tKey: 'error.shortcut.activity', Icon: Activity },
  { href: '/', tKey: 'error.shortcut.home', Icon: Home },
] as const;

/**
 * Mirrors the visual language of /not-found.tsx — same Linear grid bg,
 * radial glow, eyebrow chip and display-size headline. Swap the 404's
 * "Off the order book" headline for the offending error. No mascot/emoji —
 * the eyebrow chip + display headline carry it (clean-design house style).
 *
 * Same skeleton across 404 + 5xx so error pages feel like one family.
 */
export default function ErrorState({
  error,
  reset,
  title,
  description,
  section = 'Page',
}: ErrorStateProps) {
  const t = useT();
  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
  const [isRetrying, setIsRetrying] = useState(false);
  const { authenticated } = usePrivy();

  useEffect(() => {
    logger.error(`${section} error`, {
      error: error.message,
      digest: error.digest,
      offline: isOffline,
    });
  }, [error, section, isOffline]);

  const handleRetry = async () => {
    setIsRetrying(true);
    await new Promise((r) => setTimeout(r, 400));
    reset();
  };

  const headline = isOffline
    ? t('error.offlineHeadline')
    : title || t('error.headline');

  const subtitle = isOffline
    ? t('error.offlineSubtitle')
    : description || t('error.subtitle');

  return (
    <div className="relative min-h-[80vh] overflow-hidden flex items-center justify-center px-4 sm:px-6 lg:px-8 py-16">
      {/* Background — Linear-style grid + radial glow (mirrors not-found) */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06] text-black dark:text-white"
          style={{
            backgroundImage:
              'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage:
              'radial-gradient(ellipse 90% 60% at 50% 40%, black, transparent)',
            WebkitMaskImage:
              'radial-gradient(ellipse 90% 60% at 50% 40%, black, transparent)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(640px circle at 50% 30%, rgba(0,0,0,0.04), transparent 60%)',
          }}
        />
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background:
              'radial-gradient(640px circle at 50% 30%, rgba(255,255,255,0.04), transparent 60%)',
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center text-center">
        {/* Eyebrow chip — same shape as 404 but says 500 (or "offline") */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-100 dark:bg-[#18181D] backdrop-blur-sm mb-6">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-gray-400 dark:bg-gray-500 opacity-50 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gray-500 dark:bg-gray-400" />
          </span>
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {isOffline ? t('error.offlineChip') : t('error.chip', { section })}
          </span>
        </div>

        {/* Headline — display-size, tight tracking, single line on desktop */}
        <h1 className="font-inter-tight font-semibold text-[40px] sm:text-[60px] lg:text-[72px] leading-[1.04] tracking-[-0.03em] text-gray-900 dark:text-white mb-5 max-w-xl">
          {headline}
        </h1>

        <p className="font-inter-tight text-[15px] sm:text-base text-gray-500 dark:text-gray-400 max-w-md mb-10 leading-relaxed">
          {subtitle}
        </p>

        {/* Primary CTA — system-level button (same as 404) */}
        <button
          type="button"
          onClick={handleRetry}
          disabled={isRetrying}
          className="group inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight font-semibold text-[14px] rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100 active:scale-[0.99] transition-colors disabled:opacity-60"
        >
          <RefreshCw
            className={`w-4 h-4 ${
              isRetrying
                ? 'animate-spin'
                : 'transition-transform group-hover:rotate-180 duration-500'
            }`}
          />
          {isRetrying ? t('error.retrying') : t('error.tryAgain')}
          {!isRetrying && (
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          )}
        </button>

        {/* Secondary affordance — small tiles, identical pattern to 404 */}
        <div className="mt-14 w-full">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 mb-4">
            {t('error.jumpTo')}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {SHORTCUTS.map(({ href, tKey, Icon }) => (
              <Link
                key={`${href}-${tKey}`}
                href={href}
                className="group flex items-center justify-center gap-2 px-4 py-3 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white/40 dark:bg-white/[0.02] hover:bg-white dark:hover:bg-white/[0.04] hover:border-gray-300 dark:hover:border-white/15 transition-colors"
              >
                <Icon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" />
                <span className="font-inter-tight text-[13px] font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                  {t(tKey)}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Report bug — quiet link, only when authenticated */}
        {authenticated && (
          <Link
            href="/feedback"
            className="mt-10 inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <Bug className="w-3 h-3" />
            {t('error.reportBug')}
          </Link>
        )}

        {/* Error digest — subtle ref for support */}
        {error.digest && (
          <p className="mt-6 text-[10px] text-gray-400/50 dark:text-gray-600/50 font-mono">
            ref: {error.digest}
          </p>
        )}
      </div>
    </div>
  );
}
